import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { User, Clock, DollarSign, Upload, Camera, Loader2, Settings as SettingsIcon, Shield, CheckCircle, Lock, Mail, Brain, Sparkles, Zap, FileSignature, Eye } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { auth } from '../../utils/auth';
import { Button } from '../ui/button';
import { useDarkMode } from '../../context/DarkModeContext';
import OutlookIntegration from '../integrations/OutlookIntegration';
import GoogleIntegration from '../integrations/GoogleIntegration';
import { useTranslation } from 'react-i18next';
import { getCurrency } from '../../utils/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Translations
const translations = {
  en: {
    loadingUser: 'Loading user information...',
    nameCantBeEmpty: 'Name cannot be empty',
    nameUpdated: 'Name updated!',
    couldNotSaveName: 'Could not save name',
    signatureSaved: 'Signature saved!',
    couldNotSaveSignature: 'Could not save signature',
    profilePictureUpdated: 'Profile picture updated!',
    couldNotUploadImage: 'Could not upload image',
    selectImage: 'Please select an image',
    settingsSaved: 'Settings saved!',
    couldNotSaveSettings: 'Could not save settings',
    aiPreferencesSaved: 'AI preferences saved!',
    couldNotSaveAI: 'Could not save AI preferences',
    pageTitle: 'Settings',
    pageSubtitle: 'Manage your account and preferences',
    profile: 'Profile',
    timeTracking: 'Time Tracking',
    integrations: 'Integrations',
    aiSettings: 'AI Models',
    signature: 'Email Signature',
    security: 'Security',
    profileInfo: 'Profile Information',
    manageProfile: 'Manage your profile and preferences',
    fullName: 'Full name',
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    role: 'Role',
    notSet: 'Not set',
    saveName: 'Save',
    profilePicture: 'Profile Picture',
    uploadNewPicture: 'Upload new picture',
    supportedFormats: 'JPG, PNG or GIF. Max 5MB.',
    timeSettings: 'Time Tracking Settings',
    configureTime: 'Configure your billing and time tracking preferences',
    minTimeIncrement: 'Minimum time increment (hours)',
    minutes: 'minutes',
    hours: 'hours',
    hour: 'hour',
    defaultHourlyRate: 'Default hourly rate',
    saveSettings: 'Save settings',
    integrationsTitle: 'Integrations',
    connectServices: 'Connect to external services',
    aiPreferences: 'AI Model Preferences',
    customizeAI: 'Customize which AI models are used for different tasks',
    chatModel: 'Chat / Conversation',
    analysisModel: 'Document Analysis',
    quickModel: 'Quick Tasks',
    saveAIPreferences: 'Save AI preferences',
    emailSignature: 'Email Signature',
    signatureDescription: 'Create a signature that will be added to your emails',
    pasteSignature: 'Paste or type your signature',
    preview: 'Preview',
    saveSignature: 'Save signature'
  },
  no: {
    loadingUser: 'Laster brukerinformasjon...',
    nameCantBeEmpty: 'Navn kan ikke være tomt',
    nameUpdated: 'Navn oppdatert!',
    couldNotSaveName: 'Kunne ikke lagre navn',
    signatureSaved: 'Signatur lagret!',
    couldNotSaveSignature: 'Kunne ikke lagre signatur',
    profilePictureUpdated: 'Profilbilde oppdatert!',
    couldNotUploadImage: 'Kunne ikke laste opp bilde',
    selectImage: 'Vennligst velg et bilde',
    settingsSaved: 'Innstillinger lagret!',
    couldNotSaveSettings: 'Kunne ikke lagre innstillinger',
    aiPreferencesSaved: 'AI-preferanser lagret!',
    couldNotSaveAI: 'Kunne ikke lagre AI-preferanser',
    pageTitle: 'Innstillinger',
    pageSubtitle: 'Administrer din konto og preferanser',
    profile: 'Profil',
    timeTracking: 'Timeregistrering',
    integrations: 'Integrasjoner',
    aiSettings: 'AI-modeller',
    signature: 'E-postsignatur',
    security: 'Sikkerhet',
    profileInfo: 'Profilinformasjon',
    manageProfile: 'Administrer profilen din og preferanser',
    fullName: 'Fullt navn',
    name: 'Navn',
    email: 'E-post',
    phone: 'Telefon',
    role: 'Rolle',
    notSet: 'Ikke angitt',
    saveName: 'Lagre',
    profilePicture: 'Profilbilde',
    uploadNewPicture: 'Last opp nytt bilde',
    supportedFormats: 'JPG, PNG eller GIF. Maks 5MB.',
    timeSettings: 'Tidsregistrering',
    configureTime: 'Konfigurer fakturering og tidsregistrering',
    minTimeIncrement: 'Minste tidsinkrement (timer)',
    minutes: 'minutter',
    hours: 'timer',
    hour: 'time',
    defaultHourlyRate: 'Standard timepris',
    saveSettings: 'Lagre innstillinger',
    integrationsTitle: 'Integrasjoner',
    connectServices: 'Koble til eksterne tjenester',
    aiPreferences: 'AI-modellpreferanser',
    customizeAI: 'Tilpass hvilke AI-modeller som brukes til ulike oppgaver',
    chatModel: 'Chat / Samtale',
    analysisModel: 'Dokumentanalyse',
    quickModel: 'Raske oppgaver',
    saveAIPreferences: 'Lagre AI-preferanser',
    emailSignature: 'E-postsignatur',
    signatureDescription: 'Opprett en signatur som legges til i e-postene dine',
    pasteSignature: 'Lim inn eller skriv inn signaturen',
    preview: 'Forhåndsvisning',
    saveSignature: 'Lagre signatur'
  }
};

const SettingsViewWorldClass = ({ user, onNavigate, onProfileUpdate }) => {
  const { isDarkMode } = useDarkMode();
  const { i18n } = useTranslation();
  const lang = i18n.language === 'en' ? 'en' : 'no';
  const t = translations[lang];
  
  const [activeSection, setActiveSection] = useState('profile');
  const [timeSettings, setTimeSettings] = useState({
    min_time_increment: user?.settings?.min_time_increment || 0.25,
    default_hourly_rate: user?.settings?.default_hourly_rate || 1500
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(() => {
    const url = user?.profile_picture_url || '';
    if (url && !url.startsWith('http')) {
      return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    }
    return url;
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Currency from tenant
  const [tenantCurrency, setTenantCurrency] = useState('NOK');
  
  // Editable profile fields
  const [profileName, setProfileName] = useState(user?.name || '');
  const [savingName, setSavingName] = useState(false);
  
  // AI Model preferences state
  const [availableModels, setAvailableModels] = useState([]);
  const [aiPreferences, setAiPreferences] = useState({
    default_chat_model: 'gpt-5.1',
    default_analysis_model: 'gemini-2.5-flash',
    default_document_model: 'gemini-2.5-flash',
    default_quick_model: 'gpt-4o-mini'
  });
  const [loadingAI, setLoadingAI] = useState(false);
  const [savingAI, setSavingAI] = useState(false);
  
  // Email signature state
  const [emailSignature, setEmailSignature] = useState('');
  const [savingSignature, setSavingSignature] = useState(false);
  const signatureRef = useRef(null);
  
  // Load email signature from user settings
  useEffect(() => {
    if (user?.settings?.email_signature) {
      setEmailSignature(user.settings.email_signature);
    }
  }, [user]);
  
  // Load tenant currency
  useEffect(() => {
    const loadTenantCurrency = async () => {
      try {
        const token = auth.getToken();
        const response = await axios.get(`${API}/admin/tenant`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data?.tenant?.currency) {
          setTenantCurrency(response.data.tenant.currency);
        }
      } catch (error) {
        console.error('Error loading tenant currency:', error);
      }
    };
    loadTenantCurrency();
  }, []);
  
  // Update contentEditable when switching to signature section or when signature loads
  useEffect(() => {
    // Use a small delay to ensure the DOM element is ready
    const timer = setTimeout(() => {
      if (activeSection === 'signature' && signatureRef.current) {
        signatureRef.current.innerHTML = emailSignature || '';
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [activeSection, emailSignature]);
  
  // Load AI models and preferences
  useEffect(() => {
    const loadAIConfig = async () => {
      try {
        setLoadingAI(true);
        const token = auth.getToken();
        const headers = { Authorization: `Bearer ${token}` };
        
        const [modelsRes, prefsRes] = await Promise.all([
          axios.get(`${API}/ai/models`, { headers }),
          axios.get(`${API}/ai/preferences`, { headers })
        ]);
        
        setAvailableModels(modelsRes.data);
        setAiPreferences(prefsRes.data);
      } catch (error) {
        console.error('Error loading AI config:', error);
      } finally {
        setLoadingAI(false);
      }
    };
    
    loadAIConfig();
  }, []);

  if (!user) {
    return (
      <div className="h-full overflow-y-auto bg-gradient-to-br from-gray-50 to-gray-100/50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">{t.loadingUser}</p>
        </div>
      </div>
    );
  }

  const saveProfileName = async () => {
    if (!profileName.trim()) {
      toast.error(t.nameCantBeEmpty);
      return;
    }
    
    setSavingName(true);
    try {
      const token = auth.getToken();
      await axios.patch(
        `${API}/auth/profile`,
        { name: profileName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update localStorage
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      currentUser.name = profileName.trim();
      localStorage.setItem('user', JSON.stringify(currentUser));
      
      // Trigger update event
      window.dispatchEvent(new CustomEvent('userUpdated', { detail: currentUser }));
      
      toast.success(`✅ ${t.nameUpdated}`);
      if (onProfileUpdate) onProfileUpdate();
    } catch (error) {
      console.error('Error saving name:', error);
      toast.error(t.couldNotSaveName);
    } finally {
      setSavingName(false);
    }
  };

  const saveEmailSignature = async () => {
    setSavingSignature(true);
    try {
      const token = auth.getToken();
      await axios.patch(
        `${API}/auth/settings`,
        { email_signature: emailSignature },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update localStorage
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      currentUser.settings = currentUser.settings || {};
      currentUser.settings.email_signature = emailSignature;
      localStorage.setItem('user', JSON.stringify(currentUser));
      
      toast.success(`✅ ${t.signatureSaved}`);
    } catch (error) {
      console.error('Error saving signature:', error);
      toast.error(t.couldNotSaveSignature);
    } finally {
      setSavingSignature(false);
    }
  };

  const saveTimeSettings = async () => {
    setSaving(true);
    try {
      const token = auth.getToken();
      const response = await axios.patch(
        `${API}/auth/settings`,
        timeSettings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`✅ ${t.settingsSaved}`);
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(t.couldNotSaveSettings);
    } finally {
      setSaving(false);
    }
  };

  // Get currency info
  const currency = getCurrency(tenantCurrency);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
        toast.error(t.selectImage);
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error('File too large (max 5MB)');
        return;
      }

      // Show preview immediately
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
      setSelectedFile(file);
      
      // Auto-upload after selection
      uploadProfilePicture(file);
    }
  };

  const uploadProfilePicture = async (file: File) => {
    setUploading(true);
    try {
      const token = auth.getToken();
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API}/auth/profile-picture`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      const newUrl = response.data.profile_picture_url;
      const absoluteUrl = newUrl.startsWith('http') 
        ? newUrl 
        : `${process.env.REACT_APP_BACKEND_URL}${newUrl}`;
      setPreviewUrl(absoluteUrl);
      setSelectedFile(null);
      toast.success(`✅ ${t.profilePictureUpdated}`);

      if (onProfileUpdate) {
        onProfileUpdate();
      }
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      toast.error(t.couldNotUploadImage);
    } finally {
      setUploading(false);
    }
  };

  const handleUploadProfilePicture = async () => {
    if (!selectedFile) return;
    await uploadProfilePicture(selectedFile);
  };

  // Save AI preferences
  const saveAIPreferences = async () => {
    setSavingAI(true);
    try {
      const token = auth.getToken();
      await axios.patch(
        `${API}/ai/preferences`,
        aiPreferences,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('✅ AI-innstillinger lagret!');
    } catch (error) {
      console.error('Error saving AI preferences:', error);
      toast.error('Kunne ikke lagre AI-innstillinger');
    } finally {
      setSavingAI(false);
    }
  };

  const sections = [
    { id: 'profile', label: t.profile, icon: User },
    { id: 'signature', label: t.signature, icon: FileSignature },
    { id: 'ai', label: t.aiSettings, icon: Brain },
    { id: 'time', label: t.timeTracking, icon: Clock },
    { id: 'integrations', label: t.integrations, icon: Mail },
    { id: 'security', label: t.security, icon: Shield }
  ];

  return (
    <div className={`h-full overflow-y-auto transition-colors duration-300 ${
      isDarkMode 
        ? 'bg-gray-950' 
        : 'bg-gray-50'
    }`}>
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Header - Clean and Simple */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className={`text-2xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {t.pageTitle}
          </h1>
          <p className={`mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {t.pageSubtitle}
          </p>
        </motion.div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Navigation - Minimalist */}
          <motion.nav
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="lg:w-52 flex-shrink-0"
          >
            <div className="space-y-1">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                    activeSection === section.id
                      ? isDarkMode 
                        ? 'bg-white/10 text-white' 
                        : 'bg-white text-gray-900 shadow-sm'
                      : isDarkMode 
                        ? 'text-gray-400 hover:text-white hover:bg-white/5' 
                        : 'text-gray-600 hover:text-gray-900 hover:bg-white/60'
                  }`}
                >
                  <section.icon className={`w-4 h-4 ${
                    activeSection === section.id 
                      ? 'text-violet-500' 
                      : ''
                  }`} />
                  {section.label}
                </button>
              ))}
            </div>
          </motion.nav>

          {/* Main Content */}
          <div className="flex-1">
            {/* Profile Section */}
            {activeSection === 'profile' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`rounded-xl overflow-hidden ${
                  isDarkMode 
                    ? 'bg-gray-900 border border-gray-800' 
                    : 'bg-white border border-gray-100 shadow-sm'
                }`}
              >
                {/* Profile Header with Photo */}
                <div className={`p-6 border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-100'}`}>
                  <div className="flex items-center gap-5">
                    <div className="relative group">
                      {previewUrl ? (
                        <img
                          src={previewUrl}
                          alt="Profile"
                          className={`w-20 h-20 rounded-full object-cover ring-4 ${
                            isDarkMode ? 'ring-gray-800' : 'ring-gray-50'
                          }`}
                        />
                      ) : (
                        <div className={`w-20 h-20 rounded-full flex items-center justify-center ring-4 ${
                          isDarkMode 
                            ? 'bg-violet-900/30 ring-gray-800' 
                            : 'bg-gradient-to-br from-violet-100 to-violet-50 ring-gray-50'
                        }`}>
                          <span className="text-2xl font-semibold text-violet-500">
                            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                          </span>
                        </div>
                      )}
                      <label className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
                        {uploading ? (
                          <Loader2 className="w-5 h-5 text-white animate-spin" />
                        ) : (
                          <Camera className="w-5 h-5 text-white" />
                        )}
                        <input
                          type="file"
                          id="profile-picture"
                          accept="image/jpeg,image/jpg,image/png,image/webp"
                          onChange={handleFileSelect}
                          className="hidden"
                        />
                      </label>
                    </div>
                    <div>
                      <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {user?.name}
                      </h2>
                      <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>{user?.email}</p>
                      <span className={`inline-flex items-center px-2 py-0.5 mt-2 rounded-full text-xs font-medium ${
                        isDarkMode ? 'bg-violet-900/50 text-violet-300' : 'bg-violet-50 text-violet-700'
                      }`}>
                        {user?.role}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Profile Fields */}
                <div className="p-6 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {t.fullName}
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={profileName}
                          onChange={(e) => setProfileName(e.target.value)}
                          className={`flex-1 px-3 py-2 rounded-lg text-sm ${
                            isDarkMode 
                              ? 'bg-gray-800 border-gray-700 text-white' 
                              : 'bg-white border-gray-200 text-gray-900'
                          } border focus:ring-2 focus:ring-violet-500 focus:border-transparent`}
                        />
                        {profileName !== user.name && (
                          <button
                            onClick={saveProfileName}
                            disabled={savingName}
                            className="px-3 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50"
                          >
                            {savingName ? <Loader2 className="w-4 h-4 animate-spin" /> : t.saveName}
                          </button>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <label className={`block text-sm font-medium mb-1.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {t.email}
                      </label>
                      <input
                        type="email"
                        value={user.email || ''}
                        disabled
                        className={`w-full px-3 py-2 rounded-lg text-sm ${
                          isDarkMode 
                            ? 'bg-gray-800/50 border-gray-700 text-gray-400' 
                            : 'bg-gray-50 border-gray-200 text-gray-500'
                        } border`}
                      />
                    </div>
                    
                    <div>
                      <label className={`block text-sm font-medium mb-1.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {t.phone}
                      </label>
                      <input
                        type="tel"
                        value={user.phone || ''}
                        disabled
                        placeholder={t.notSet}
                        className={`w-full px-3 py-2 rounded-lg text-sm ${
                          isDarkMode 
                            ? 'bg-gray-800/50 border-gray-700 text-gray-400' 
                            : 'bg-gray-50 border-gray-200 text-gray-500'
                        } border`}
                      />
                    </div>
                    
                    <div>
                      <label className={`block text-sm font-medium mb-1.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {t.role}
                      </label>
                      <input
                        type="text"
                        value={user.role || ''}
                        disabled
                        className={`w-full px-3 py-2 rounded-lg text-sm ${
                          isDarkMode 
                            ? 'bg-gray-800/50 border-gray-700 text-gray-400' 
                            : 'bg-gray-50 border-gray-200 text-gray-500'
                        } border`}
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Email Signature Section */}
            {activeSection === 'signature' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`rounded-xl p-6 ${
                  isDarkMode 
                    ? 'bg-gray-900 border border-gray-800' 
                    : 'bg-white border border-gray-100 shadow-sm'
                }`}
              >
                <div className="mb-6">
                  <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {t.emailSignature}
                  </h2>
                  <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {t.signatureDescription}
                  </p>
                </div>

                {/* Rich Text Editor */}
                <div className="space-y-3">
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t.pasteSignature}
                  </label>
                  <div
                    ref={signatureRef}
                    contentEditable
                    onInput={(e) => setEmailSignature(e.currentTarget.innerHTML)}
                    onPaste={(e) => {
                      setTimeout(() => {
                        if (e.currentTarget) {
                          setEmailSignature(e.currentTarget.innerHTML);
                        }
                      }, 0);
                    }}
                    className={`w-full min-h-[140px] p-4 rounded-lg border focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all overflow-auto ${
                      isDarkMode 
                        ? 'bg-gray-800 border-gray-700 text-gray-200' 
                        : 'bg-gray-50 border-gray-200 text-gray-800'
                    }`}
                    style={{ outline: 'none' }}
                    suppressContentEditableWarning={true}
                  />
                  <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    Tip: Kopier signaturen fra Outlook og lim inn. Formateringen beholdes.
                  </p>
                </div>

                {/* HTML Editor (Advanced) */}
                <details className={`mt-4 rounded-lg ${
                  isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'
                }`}>
                  <summary className={`px-4 py-3 cursor-pointer text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Avansert: Rediger HTML
                  </summary>
                  <div className="px-4 pb-4 pt-2">
                    <textarea
                      value={emailSignature}
                      onChange={(e) => {
                        setEmailSignature(e.target.value);
                        // Update the contentEditable div
                        if (signatureRef.current) {
                          signatureRef.current.innerHTML = e.target.value;
                        }
                      }}
                      placeholder={`<p>Med vennlig hilsen,</p>
<p><strong>${user?.name || 'Ditt navn'}</strong></p>
<p>Advokat</p>`}
                      className={`w-full h-40 p-4 rounded-xl border font-mono text-xs resize-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all ${
                        isDarkMode 
                          ? 'bg-gray-900 border-gray-600 text-gray-200 placeholder-gray-500' 
                          : 'bg-white border-gray-200 text-gray-800 placeholder-gray-400'
                      }`}
                    />
                  </div>
                </details>

                {/* Preview */}
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-3">
                    <label className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Forhåndsvisning
                    </label>
                    {emailSignature && (
                      <button
                        onClick={() => {
                          setEmailSignature('');
                          if (signatureRef.current) {
                            signatureRef.current.innerHTML = '';
                          }
                        }}
                        className="text-sm text-red-500 hover:text-red-600"
                      >
                        Tøm
                      </button>
                    )}
                  </div>
                  
                  {emailSignature ? (
                    <div className={`rounded-lg p-4 ${
                      isDarkMode 
                        ? 'bg-gray-800 border border-gray-700' 
                        : 'bg-gray-50 border border-gray-200'
                    }`}>
                      <div 
                        className={`prose prose-sm max-w-none ${isDarkMode ? 'prose-invert' : ''}`}
                        dangerouslySetInnerHTML={{ __html: emailSignature }}
                      />
                    </div>
                  ) : (
                    <div className={`rounded-lg p-6 text-center border border-dashed ${
                      isDarkMode ? 'border-gray-700' : 'border-gray-200'
                    }`}>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        Ingen signatur ennå
                      </p>
                    </div>
                  )}
                </div>
                  
                {/* Quick templates */}
                <div className="mt-4">
                  <p className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    Hurtigmaler:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { name: 'Enkel', html: `<p>Med vennlig hilsen,</p><p><strong>${user?.name || 'Ditt navn'}</strong></p><p>Advokat</p>` },
                      { name: 'Med kontakt', html: `<p>Med vennlig hilsen,</p><p><strong>${user?.name || 'Ditt navn'}</strong></p><p>Advokat</p><p style="color: #666; font-size: 12px;">E-post: ${user?.email || 'din@epost.no'}</p>` },
                    ].map((template) => (
                      <button
                        key={template.name}
                        onClick={() => {
                          setEmailSignature(template.html);
                          if (signatureRef.current) signatureRef.current.innerHTML = template.html;
                        }}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                          isDarkMode 
                            ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' 
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {template.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Save button */}
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={saveEmailSignature}
                    disabled={savingSignature}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors disabled:opacity-50"
                  >
                    {savingSignature ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4" />
                    )}
                    Lagre signatur
                  </button>
                </div>
              </motion.div>
            )}

            {/* AI Models Section */}
            {activeSection === 'ai' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`rounded-xl p-6 ${
                  isDarkMode 
                    ? 'bg-gray-900 border border-gray-800' 
                    : 'bg-white border border-gray-100 shadow-sm'
                }`}
              >
                <div className="mb-6">
                  <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    AI-konfigurasjon
                  </h2>
                  <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Velg foretrukne AI-modeller for ulike oppgaver
                  </p>
                </div>

                {loadingAI ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
                    <span className={`ml-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Laster modeller...</span>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Chat Model */}
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      className={`rounded-xl p-6 border ${
                        isDarkMode 
                          ? 'bg-white/5 border-white/10' 
                          : 'bg-gradient-to-br from-gray-50 to-white border-gray-100'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <Sparkles className="w-5 h-5 text-purple-500" />
                        <label className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                          AI Chat / Workspace
                        </label>
                      </div>
                      <select
                        value={aiPreferences.default_chat_model}
                        onChange={(e) => setAiPreferences({
                          ...aiPreferences,
                          default_chat_model: e.target.value
                        })}
                        className={`w-full px-4 py-3 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${
                          isDarkMode 
                            ? 'bg-gray-800 border-gray-700 text-white' 
                            : 'bg-white border-gray-200 text-gray-900'
                        }`}
                      >
                        {availableModels.map(model => (
                          <option key={model.id} value={model.id}>
                            {model.name} ({model.provider}) - {model.description}
                          </option>
                        ))}
                      </select>
                      <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Brukes i AI Workspace og chat-funksjonalitet
                      </p>
                    </motion.div>

                    {/* Analysis Model */}
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.35 }}
                      className={`rounded-xl p-6 border ${
                        isDarkMode 
                          ? 'bg-white/5 border-white/10' 
                          : 'bg-gradient-to-br from-gray-50 to-white border-gray-100'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <Zap className="w-5 h-5 text-amber-500" />
                        <label className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                          E-post- og saksanalyse
                        </label>
                      </div>
                      <select
                        value={aiPreferences.default_analysis_model}
                        onChange={(e) => setAiPreferences({
                          ...aiPreferences,
                          default_analysis_model: e.target.value
                        })}
                        className={`w-full px-4 py-3 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${
                          isDarkMode 
                            ? 'bg-gray-800 border-gray-700 text-white' 
                            : 'bg-white border-gray-200 text-gray-900'
                        }`}
                      >
                        {availableModels.map(model => (
                          <option key={model.id} value={model.id}>
                            {model.name} ({model.provider}) - {model.description}
                          </option>
                        ))}
                      </select>
                      <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Brukes for AI-analyse av e-poster og juridiske dokumenter
                      </p>
                    </motion.div>

                    {/* Document Model */}
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                      className={`rounded-xl p-6 border ${
                        isDarkMode 
                          ? 'bg-white/5 border-white/10' 
                          : 'bg-gradient-to-br from-gray-50 to-white border-gray-100'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <Upload className="w-5 h-5 text-blue-500" />
                        <label className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                          Dokumentbehandling (PDF, bilder)
                        </label>
                      </div>
                      <select
                        value={aiPreferences.default_document_model}
                        onChange={(e) => setAiPreferences({
                          ...aiPreferences,
                          default_document_model: e.target.value
                        })}
                        className={`w-full px-4 py-3 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${
                          isDarkMode 
                            ? 'bg-gray-800 border-gray-700 text-white' 
                            : 'bg-white border-gray-200 text-gray-900'
                        }`}
                      >
                        {availableModels.map(model => (
                          <option key={model.id} value={model.id}>
                            {model.name} ({model.provider}) - {model.description}
                          </option>
                        ))}
                      </select>
                      <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Anbefalt: Gemini for stort kontekstvindu og multimodal støtte
                      </p>
                    </motion.div>

                    {/* Quick Model */}
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.45 }}
                      className={`rounded-xl p-6 border ${
                        isDarkMode 
                          ? 'bg-white/5 border-white/10' 
                          : 'bg-gradient-to-br from-gray-50 to-white border-gray-100'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <Zap className="w-5 h-5 text-green-500" />
                        <label className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                          Raske oppgaver og klassifisering
                        </label>
                      </div>
                      <select
                        value={aiPreferences.default_quick_model}
                        onChange={(e) => setAiPreferences({
                          ...aiPreferences,
                          default_quick_model: e.target.value
                        })}
                        className={`w-full px-4 py-3 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${
                          isDarkMode 
                            ? 'bg-gray-800 border-gray-700 text-white' 
                            : 'bg-white border-gray-200 text-gray-900'
                        }`}
                      >
                        {availableModels.map(model => (
                          <option key={model.id} value={model.id}>
                            {model.name} ({model.provider}) - {model.description}
                          </option>
                        ))}
                      </select>
                      <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Brukes for enkle, raske operasjoner der hastighet er viktig
                      </p>
                    </motion.div>

                    {/* Save Button */}
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                      className="flex justify-end pt-4"
                    >
                      <Button
                        onClick={saveAIPreferences}
                        disabled={savingAI}
                        className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white px-6 py-3 rounded-xl font-medium shadow-lg hover:shadow-xl transition-all"
                      >
                        {savingAI ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Lagrer...
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Lagre AI-innstillinger
                          </>
                        )}
                      </Button>
                    </motion.div>

                    {/* Info box */}
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.55 }}
                      className={`rounded-xl p-4 ${isDarkMode ? 'bg-purple-500/10' : 'bg-purple-50'}`}
                    >
                      <p className={`text-sm flex items-start gap-2 ${isDarkMode ? 'text-purple-200' : 'text-purple-700'}`}>
                        <Brain className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>
                          Lexy bruker avanserte AI-modeller fra OpenAI, Google (Gemini) og Anthropic. 
                          Velg modeller basert på dine behov for hastighet, nøyaktighet og kostnad.
                        </span>
                      </p>
                    </motion.div>
                  </div>
                )}
              </motion.div>
            )}

            {/* Time Settings Section */}
            {activeSection === 'time' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`rounded-xl p-6 ${
                  isDarkMode 
                    ? 'bg-gray-900 border border-gray-800' 
                    : 'bg-white border border-gray-100 shadow-sm'
                }`}
              >
                <div className="mb-6">
                  <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {t.timeSettings}
                  </h2>
                  <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {t.configureTime}
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {t.minTimeIncrement}
                    </label>
                    <select
                      value={timeSettings.min_time_increment}
                      onChange={(e) => setTimeSettings({
                        ...timeSettings,
                        min_time_increment: parseFloat(e.target.value)
                      })}
                      className={`w-full px-3 py-2 rounded-lg text-sm ${
                        isDarkMode 
                          ? 'bg-gray-800 border-gray-700 text-white' 
                          : 'bg-white border-gray-200 text-gray-900'
                      } border focus:ring-2 focus:ring-violet-500`}
                    >
                      <option value={0.1}>6 {t.minutes} (0.1 {t.hours})</option>
                      <option value={0.25}>15 {t.minutes} (0.25 {t.hours})</option>
                      <option value={0.5}>30 {t.minutes} (0.5 {t.hours})</option>
                      <option value={1}>60 {t.minutes} (1 {t.hour})</option>
                    </select>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {t.defaultHourlyRate} ({currency.code})
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        value={timeSettings.default_hourly_rate}
                        onChange={(e) => setTimeSettings({
                          ...timeSettings,
                          default_hourly_rate: parseInt(e.target.value) || 0
                        })}
                        className={`w-full px-3 py-2 pr-12 rounded-lg text-sm ${
                          isDarkMode 
                            ? 'bg-gray-800 border-gray-700 text-white' 
                            : 'bg-white border-gray-200 text-gray-900'
                        } border focus:ring-2 focus:ring-violet-500`}
                      />
                      <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {currency.symbol}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={saveTimeSettings}
                    disabled={saving}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors disabled:opacity-50"
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4" />
                    )}
                    Lagre innstillinger
                  </button>
                </div>
              </motion.div>
            )}

            {/* Integrations Section */}
            {activeSection === 'integrations' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`rounded-xl p-6 ${
                  isDarkMode 
                    ? 'bg-gray-900 border border-gray-800' 
                    : 'bg-white border border-gray-100 shadow-sm'
                }`}
              >
                <div className="mb-6">
                  <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Integrasjoner
                  </h2>
                  <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Koble til eksterne tjenester
                  </p>
                </div>
                  
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <OutlookIntegration />
                  <GoogleIntegration />
                </div>
              </motion.div>
            )}

            {/* Security Section */}
            {activeSection === 'security' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`rounded-xl p-6 ${
                  isDarkMode 
                    ? 'bg-gray-900 border border-gray-800' 
                    : 'bg-white border border-gray-100 shadow-sm'
                }`}
              >
                <div className="mb-6">
                  <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Sikkerhet
                  </h2>
                  <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Administrer sikkerhetsinnstillinger
                  </p>
                </div>

                <div className="space-y-4">
                  {/* Password */}
                  <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
                        }`}>
                          <Lock className={`w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                        </div>
                        <div>
                          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Passord</p>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Endre ditt passord
                          </p>
                        </div>
                      </div>
                      <button className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        isDarkMode 
                          ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}>
                        Endre
                      </button>
                    </div>
                  </div>

                  {/* 2FA */}
                  <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          isDarkMode ? 'bg-green-900/30' : 'bg-green-100'
                        }`}>
                          <Shield className={`w-5 h-5 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
                        </div>
                        <div>
                          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            To-faktor autentisering
                          </p>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Ekstra sikkerhet for kontoen
                          </p>
                        </div>
                      </div>
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                        isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'
                      }`}>
                        Kommer snart
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsViewWorldClass;
